
import {isNullOrUndefined} from "util";

export class TractorBeamStateMatrix<T> {
    private readonly _reels:number;
    private readonly _symbols:number;
    private _stateMatrix:T[][];

    /**
     * SymbolStateMatrix constructor.
     *
     * @param reels Number of reels
     * @param symbols Total number of symbols in each reel
     * @param visibleRange Start index to end index of visible range, inclusive.
     * @param initState T state that will be used to initialize the matrix.
     */
    constructor(reels:number, symbols:number, visibleRange:[number, number], initState:T) {
        this._reels = reels;
        this._symbols = symbols;
        this._stateMatrix = [];
        for(let i:number = 0; i < this._reels; i++) {
            this._stateMatrix.push(new Array(this._symbols));
        }
        this.populate(initState)
    }

    /**
     * Populates entire matrix with the same state. Useful for resetting.
     *
     * @param state
     */
    public populate(state:T) {
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                this.setStateAtAbsolute(reelIndex, symbolIndex, state);
            }
        }
    }


    /**
     * Sets state at coordinates without taking "visibleSymbolsOffset" into account.
     * So no negative indices!
     *
     * @param reelIndex Zero based reelIndex
     * @param symbolIndex Zero based symbolIndex
     * @param state The state to set
     */
    public setStateAtAbsolute(reelIndex:number, symbolIndex:number, state:T):void {
        this._stateMatrix[reelIndex][symbolIndex] = state;
    }

    /**
     * Returns state at coordinates with "visibleSymbolsOffset" taken into account.
     *
     * So you can get symbols with a negative index.
     * If the absolute coordinate is 0,0 then you would get it with 0,-2 with this function.
     *
     * @param reelIndex ReelIndex
     * @param symbolIndex SymbolIndex
     * @returns {string} State
     */
    public getStateAt(reelIndex:number, symbolIndex:number):T {
        return this.getStateAtAbsolute(reelIndex, symbolIndex);
    }

    /**
     * Returns state at coordinates without taking "visibleSymbolsOffset" into account.
     * So no negative indices!
     *
     * @param reelIndex Zero based reelIndex
     * @param symbolIndex Zero based symbolIndex
     * @returns {string} State
     */
    public getStateAtAbsolute(reelIndex:number, symbolIndex:number):T {
        return this._stateMatrix[reelIndex][symbolIndex];
    }

    /**
     * Sets state at coordinates with "visibleSymbolsOffset" taken into account.
     *
     * So you can set symbols with a negative index.
     * If the absolute coordinate is 0,0 then you would set it with 0,-2 with this function.
     *
     * @param reelIndex
     * @param symbolIndex
     * @param state
     */
    public setStateAt(reelIndex:number, symbolIndex:number, state:T):void {
        this.setStateAtAbsolute(reelIndex, symbolIndex, state);
    }

    /**
     * Gets all matching state coordinates and returns them in a array of [number,number] tuples.
     *
     * @param state State to filter.
     * @returns {[number,number][]} Array of [number,number] Coordinate tuples for all matching states.
     */
    public getAllCoordinatesWithState(state:T):[number, number][] {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                if(this.getStateAtAbsolute(reelIndex, symbolIndex) == state) {
                    result.push([reelIndex, symbolIndex]);
                }
            }
        }
        return result;
    }


}
